/**
 * Social Media Photos Component
 * Handles video playback, hover interactions, and user controls
 */
class SocialMediaPhotosManager {
  constructor() {
    this.isMobile = window.innerWidth <= 767;
    this.videoPlayers = new Map();
    // Shared mute state — user-toggled mute persists across videos
    // so unmuting one video means swiped-to videos also play with sound.
    this.sharedMuteState = true;
    // Tracks the player currently auto-playing on mobile (swipe-into-view).
    this.currentlyPlayingPlayer = null;
    this.mobileIO = null;
  }

  /**
   * Initialize all video players in the section
   */
  initializeVideoPlayers(container) {
    const videoWrappers = container.querySelectorAll('.social-media-photos__video-wrapper');
    
    videoWrappers.forEach((wrapper, index) => {
      const video = wrapper.querySelector('.social-media-photos__video');
      const playButton = wrapper.querySelector('.social-media-photos__play-button');
      const muteButton = wrapper.querySelector('.social-media-photos__mute-button');
      
      if (!video || !playButton) return;

      video.defaultMuted = true;
      video.muted = true;

      // Find the parent item element
      const itemElement = wrapper.closest('.social-media-photos__item');

      const playerState = {
        video,
        playButton,
        muteButton,
        wrapper,
        itemElement,
        isPlaying: false,
        isMuted: this.sharedMuteState,
        hoverTimeout: null,
        userHasInteracted: false,
        // Last intersection ratio reported by the mobile autoplay observer.
        lastRatio: 0
      };

      // Sync video element to shared mute state at init.
      video.muted = this.sharedMuteState;

      if (muteButton) {
        this.updateMuteButton(muteButton, playerState.isMuted);
      }

      this.checkAutoplayState(playerState);
      this.setupVideoEventListeners(playerState);
      this.setupInteractionListeners(playerState);
      this.setupMuteButton(playerState);

      this.videoPlayers.set(video, playerState);
    });

    if (this.isMobile) {
      this.setupMobileSwipeAutoplay();
    }
  }

  /**
   * Mobile-only: autoplay the most-visible video in the slider as the user
   * swipes. Uses a single IntersectionObserver rooted on the viewport with
   * multiple thresholds so we get continuous visibility updates without
   * scroll listeners. When the section scrolls fully off-screen, the active
   * video is paused so audio doesn't keep playing in the background.
   */
  setupMobileSwipeAutoplay() {
    if (!('IntersectionObserver' in window) || this.videoPlayers.size === 0) return;

    const VISIBILITY_THRESHOLD = 0.6;

    this.mobileIO = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const player = this.videoPlayers.get(entry.target);
        if (player) player.lastRatio = entry.intersectionRatio;
      }

      let best = null;
      this.videoPlayers.forEach((p) => {
        if (p.lastRatio >= VISIBILITY_THRESHOLD && (!best || p.lastRatio > best.lastRatio)) {
          best = p;
        }
      });

      if (best) {
        if (best !== this.currentlyPlayingPlayer) {
          if (this.currentlyPlayingPlayer) {
            this.pauseAndResetPlayer(this.currentlyPlayingPlayer);
          }
          this.currentlyPlayingPlayer = best;
          this.playVideo(best);
        }
      } else if (this.currentlyPlayingPlayer) {
        this.pauseAndResetPlayer(this.currentlyPlayingPlayer);
        this.currentlyPlayingPlayer = null;
      }
    }, {
      root: null,
      threshold: [0, 0.25, 0.5, 0.6, 0.75, 1.0]
    });

    this.videoPlayers.forEach((_player, video) => this.mobileIO.observe(video));
  }

  /**
   * Pause a player and rewind to the start. Used when the player is no
   * longer the active mobile slide.
   */
  pauseAndResetPlayer(playerState) {
    const { video } = playerState;
    if (!video.paused) video.pause();
    if (video.currentTime !== 0) {
      try { video.currentTime = 0; } catch (_) { /* ignore seek errors on un-hydrated sources */ }
    }
    playerState.isPlaying = false;
    this.updateItemActiveState(playerState.itemElement, false);
    if (this.isMobile) {
      this.updatePlayButton(playerState.playButton, 'play');
    }
  }

  /**
   * Check if video is autoplaying and update state
   */
  checkAutoplayState(playerState) {
    const { video, itemElement } = playerState;
    
    if (video.autoplay && video.readyState >= 2) {
      playerState.isPlaying = true;
      this.updateItemActiveState(itemElement, true);
    }

    // Listen for play/pause events to sync state
    video.addEventListener('play', () => {
      playerState.isPlaying = true;
      this.updateItemActiveState(itemElement, true);
    });

    video.addEventListener('pause', () => {
      playerState.isPlaying = false;
      this.updateItemActiveState(itemElement, false);
    });
  }

  /**
   * Set up video-specific event listeners
   */
  setupVideoEventListeners(playerState) {
    const { video, playButton, muteButton } = playerState;

    // Video ended handler
    video.addEventListener('ended', () => {
      const { itemElement } = playerState;
      if (this.isMobile) {
        this.resetPlayButton(playButton);
      }
      playerState.isPlaying = false;
      this.updateItemActiveState(itemElement, false);
    });

    // Sync mute state when video muted attribute changes
    video.addEventListener('volumechange', () => {
      playerState.isMuted = video.muted;
      if (muteButton) {
        this.updateMuteButton(muteButton, playerState.isMuted);
      }
    });

    // Mute toggle on double click
    video.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.toggleMute(playerState);
    });
  }

  /**
   * Set up interaction listeners (hover, click)
   */
  setupInteractionListeners(playerState) {
    const { wrapper } = playerState;

    // Add user interaction detection
    wrapper.addEventListener('click', () => {
      playerState.userHasInteracted = true;
    }, { once: true });

    if (this.isMobile) {
      this.setupMobileControls(playerState);
    } else {
      this.setupDesktopControls(playerState);
    }
  }

  /**
   * Set up desktop hover and click controls
   */
  setupDesktopControls(playerState) {
    const { wrapper } = playerState;

    // Hover to play
    wrapper.addEventListener('mouseenter', () => {
      this.handleHoverEnter(playerState);
    });

    // Stop on mouse leave
    wrapper.addEventListener('mouseleave', () => {
      this.handleHoverLeave(playerState);
    });

    // Click to toggle
    wrapper.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.togglePlayback(playerState);
    });
  }

  /**
   * Set up mobile touch controls
   */
  setupMobileControls(playerState) {
    const { playButton } = playerState;

    playButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.togglePlayback(playerState);
    });
  }

  /**
   * Handle mouse enter (hover to play)
   */
  handleHoverEnter(playerState) {
    const { hoverTimeout } = playerState;

    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Delay play to prevent rapid hover conflicts
    playerState.hoverTimeout = setTimeout(() => {
      if (playerState.video.paused) {
        this.playVideo(playerState);
      }
    }, 100);
  }

  /**
   * Handle mouse leave (stop video)
   */
  handleHoverLeave(playerState) {
    const { hoverTimeout } = playerState;

    // Clear timeout if mouse leaves before play
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    if (!playerState.video.paused) {
      this.pauseVideo(playerState);
      playerState.video.currentTime = 0; // Reset to beginning
    }
  }

  /**
   * Toggle video playback
   */
  togglePlayback(playerState) {
    if (playerState.video.paused) {
      this.playVideo(playerState);
    } else {
      this.pauseVideo(playerState);
    }
  }

  /**
   * Play video with error handling. Always reapplies the shared mute state
   * so that swipe-induced playback inherits the user's last mute choice.
   */
  playVideo(playerState) {
    const { video, playButton, itemElement } = playerState;

    video.muted = this.sharedMuteState;
    playerState.isMuted = this.sharedMuteState;

    video.play().then(() => {
      playerState.isPlaying = true;
      this.updateItemActiveState(itemElement, true);

      if (this.isMobile) {
        this.updatePlayButton(playButton, 'pause');
      }
    }).catch(() => {
      // Browser blocked playback (typically only when unmuted without a
      // user gesture). Fall back to muted and try once more so the video
      // still plays — sound will resume on the next user-initiated swipe
      // after they tap unmute.
      if (!video.muted) {
        video.muted = true;
        playerState.isMuted = true;
        if (playerState.muteButton) {
          this.updateMuteButton(playerState.muteButton, true);
        }
        video.play().catch(() => {
          playerState.userHasInteracted = false;
        });
      } else {
        playerState.userHasInteracted = false;
      }
    });
  }

  /**
   * Pause video
   */
  pauseVideo(playerState) {
    const { video, playButton, itemElement } = playerState;

    video.pause();
    playerState.isPlaying = false;
    this.updateItemActiveState(itemElement, false);
    
    if (this.isMobile) {
      this.updatePlayButton(playButton, 'play');
    }
  }

  /**
   * Set up mute button event listener
   */
  setupMuteButton(playerState) {
    const { muteButton } = playerState;
    
    if (!muteButton) return;

    muteButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleMute(playerState);
    });
  }

  /**
   * Toggle mute. The new mute state becomes the shared state across all
   * videos in the section, so subsequent autoplayed videos (swipe-into-view
   * on mobile, hover on desktop) inherit the user's choice.
   */
  toggleMute(playerState) {
    this.sharedMuteState = !playerState.isMuted;

    this.videoPlayers.forEach((p) => {
      p.video.muted = this.sharedMuteState;
      p.isMuted = this.sharedMuteState;
      if (p.muteButton) {
        this.updateMuteButton(p.muteButton, this.sharedMuteState);
      }
    });
  }

  /**
   * Update mute button icon based on mute state
   */
  updateMuteButton(button, isMuted) {
    // Muted state icon (speaker with sound waves and X overlay)
    const mutedStateIcon = `
      <svg height="24" viewBox="0 0 24 24" width="24"><path d="M11.60 2.08L11.48 2.14L3.91 6.68C3.02 7.21 2.28 7.97 1.77 8.87C1.26 9.77 1.00 10.79 1 11.83V12.16L1.01 12.56C1.07 13.52 1.37 14.46 1.87 15.29C2.38 16.12 3.08 16.81 3.91 17.31L11.48 21.85C11.63 21.94 11.80 21.99 11.98 21.99C12.16 22.00 12.33 21.95 12.49 21.87C12.64 21.78 12.77 21.65 12.86 21.50C12.95 21.35 13 21.17 13 21V3C12.99 2.83 12.95 2.67 12.87 2.52C12.80 2.37 12.68 2.25 12.54 2.16C12.41 2.07 12.25 2.01 12.08 2.00C11.92 1.98 11.75 2.01 11.60 2.08ZM4.94 8.4V8.40L11 4.76V19.23L4.94 15.6C4.38 15.26 3.92 14.80 3.58 14.25C3.24 13.70 3.05 13.07 3.00 12.43L3 12.17V11.83C2.99 11.14 3.17 10.46 3.51 9.86C3.85 9.25 4.34 8.75 4.94 8.4ZM21.29 8.29L19 10.58L16.70 8.29L16.63 8.22C16.43 8.07 16.19 7.99 15.95 8.00C15.70 8.01 15.47 8.12 15.29 8.29C15.12 8.47 15.01 8.70 15.00 8.95C14.99 9.19 15.07 9.43 15.22 9.63L15.29 9.70L17.58 12L15.29 14.29C15.19 14.38 15.12 14.49 15.06 14.61C15.01 14.73 14.98 14.87 14.98 15.00C14.98 15.13 15.01 15.26 15.06 15.39C15.11 15.51 15.18 15.62 15.28 15.71C15.37 15.81 15.48 15.88 15.60 15.93C15.73 15.98 15.86 16.01 15.99 16.01C16.12 16.01 16.26 15.98 16.38 15.93C16.50 15.87 16.61 15.80 16.70 15.70L19 13.41L21.29 15.70L21.36 15.77C21.56 15.93 21.80 16.01 22.05 15.99C22.29 15.98 22.53 15.88 22.70 15.70C22.88 15.53 22.98 15.29 22.99 15.05C23.00 14.80 22.93 14.56 22.77 14.36L22.70 14.29L20.41 12L22.70 9.70C22.80 9.61 22.87 9.50 22.93 9.38C22.98 9.26 23.01 9.12 23.01 8.99C23.01 8.86 22.98 8.73 22.93 8.60C22.88 8.48 22.81 8.37 22.71 8.28C22.62 8.18 22.51 8.11 22.39 8.06C22.26 8.01 22.13 7.98 22.00 7.98C21.87 7.98 21.73 8.01 21.61 8.06C21.49 8.12 21.38 8.19 21.29 8.29Z" fill="white"></path></svg>
    `;
    
    // Unmuted state icon (speaker with sound waves)
    const unmutedStateIcon = `
      <svg height="24" viewBox="0 0 24 24" width="24"><path d="M 11.60 2.08 L 11.48 2.14 L 3.91 6.68 C 3.02 7.21 2.28 7.97 1.77 8.87 C 1.26 9.77 1.00 10.79 1 11.83 V 12.16 L 1.01 12.56 C 1.07 13.52 1.37 14.46 1.87 15.29 C 2.38 16.12 3.08 16.81 3.91 17.31 L 11.48 21.85 C 11.63 21.94 11.80 21.99 11.98 21.99 C 12.16 22.00 12.33 21.95 12.49 21.87 C 12.64 21.78 12.77 21.65 12.86 21.50 C 12.95 21.35 13 21.17 13 21 V 3 C 12.99 2.83 12.95 2.67 12.87 2.52 C 12.80 2.37 12.68 2.25 12.54 2.16 C 12.41 2.07 12.25 2.01 12.08 2.00 C 11.92 1.98 11.75 2.01 11.60 2.08 Z" fill="#fff"></path><path d=" M 15.53 7.05 C 15.35 7.22 15.25 7.45 15.24 7.70 C 15.23 7.95 15.31 8.19 15.46 8.38 L 15.53 8.46 L 15.70 8.64 C 16.09 9.06 16.39 9.55 16.61 10.08 L 16.70 10.31 C 16.90 10.85 17 11.42 17 12 L 16.99 12.24 C 16.96 12.73 16.87 13.22 16.70 13.68 L 16.61 13.91 C 16.36 14.51 15.99 15.07 15.53 15.53 C 15.35 15.72 15.25 15.97 15.26 16.23 C 15.26 16.49 15.37 16.74 15.55 16.92 C 15.73 17.11 15.98 17.21 16.24 17.22 C 16.50 17.22 16.76 17.12 16.95 16.95 C 17.6 16.29 18.11 15.52 18.46 14.67 L 18.59 14.35 C 18.82 13.71 18.95 13.03 18.99 12.34 L 19 12 C 18.99 11.19 18.86 10.39 18.59 9.64 L 18.46 9.32 C 18.15 8.57 17.72 7.89 17.18 7.3 L 16.95 7.05 L 16.87 6.98 C 16.68 6.82 16.43 6.74 16.19 6.75 C 15.94 6.77 15.71 6.87 15.53 7.05" fill="#fff" transform="translate(18, 12) scale(1) translate(-18,-12)"></path><path d="M18.36 4.22C18.18 4.39 18.08 4.62 18.07 4.87C18.05 5.12 18.13 5.36 18.29 5.56L18.36 5.63L18.66 5.95C19.36 6.72 19.91 7.60 20.31 8.55L20.47 8.96C20.82 9.94 21 10.96 21 11.99L20.98 12.44C20.94 13.32 20.77 14.19 20.47 15.03L20.31 15.44C19.86 16.53 19.19 17.52 18.36 18.36C18.17 18.55 18.07 18.80 18.07 19.07C18.07 19.33 18.17 19.59 18.36 19.77C18.55 19.96 18.80 20.07 19.07 20.07C19.33 20.07 19.59 19.96 19.77 19.77C20.79 18.75 21.61 17.54 22.16 16.20L22.35 15.70C22.72 14.68 22.93 13.62 22.98 12.54L23 12C22.99 10.73 22.78 9.48 22.35 8.29L22.16 7.79C21.67 6.62 20.99 5.54 20.15 4.61L19.77 4.22L19.70 4.15C19.51 3.99 19.26 3.91 19.02 3.93C18.77 3.94 18.53 4.04 18.36 4.22 Z" fill="#fff" transform="translate(22, 12) scale(1) translate(-22, -12)"></path></svg>
    `;

    const iconWrapper = button.querySelector('.svg-wrapper');
    if (iconWrapper) {
      // Show icon that represents current state: muted = muted icon, unmuted = unmuted icon
      iconWrapper.innerHTML = isMuted ? mutedStateIcon : unmutedStateIcon;
    } else {
      // Fallback if structure is different
      button.innerHTML = `
        <div class="svg-wrapper mute-button-icon">
          ${isMuted ? mutedStateIcon : unmutedStateIcon}
        </div>
      `;
    }

    // Update aria-label to indicate what action will happen when clicked
    button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }

  /**
   * Update active state class on item element
   */
  updateItemActiveState(itemElement, isActive) {
    if (!itemElement) return;
    
    if (isActive) {
      itemElement.classList.add('social-media-photos__item-video--is-active');
    } else {
      itemElement.classList.remove('social-media-photos__item-video--is-active');
    }
  }

  /**
   * Update play button icon
   */
  updatePlayButton(button, state) {
    const playIcon = `
      <svg class="social-media-photos__play-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    `;
    
    const pauseIcon = `
      <svg class="social-media-photos__pause-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
      </svg>
    `;

    if (state === 'play') {
      button.style.opacity = '1';
      button.innerHTML = playIcon;
    } else {
      button.style.opacity = '0.7';
      button.innerHTML = pauseIcon;
    }
  }

  /**
   * Reset play button to initial state
   */
  resetPlayButton(button) {
    button.style.opacity = '1';
    button.innerHTML = `
      <svg class="social-media-photos__play-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    `;
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.mobileIO) {
      this.mobileIO.disconnect();
      this.mobileIO = null;
    }
    this.videoPlayers.forEach((playerState) => {
      if (playerState.hoverTimeout) {
        clearTimeout(playerState.hoverTimeout);
      }
    });
    this.videoPlayers.clear();
    this.currentlyPlayingPlayer = null;
  }
}

function parseLazyVideoSources(attributeValue) {
  if (!attributeValue || !String(attributeValue).trim()) return [];
  return attributeValue
    .split(',')
    .map((pair) => {
      const idx = pair.indexOf('|');
      if (idx === -1) return null;
      return { url: pair.slice(0, idx), type: pair.slice(idx + 1) };
    })
    .filter(Boolean);
}

function hydrateLazyVideos(container) {
  const videos = container.querySelectorAll('video[data-lazy-video-sources]');
  const autoplayVideos = [];
  videos.forEach((video) => {
    const raw = video.getAttribute('data-lazy-video-sources');
    if (!raw) return;
    const wantAutoplay = video.getAttribute('data-lazy-autoplay') === 'true';
    video.removeAttribute('data-lazy-autoplay');
    parseLazyVideoSources(raw).forEach(({ url, type }) => {
      const source = document.createElement('source');
      source.src = url;
      if (type) source.type = type;
      video.appendChild(source);
    });
    video.removeAttribute('data-lazy-video-sources');
    if (wantAutoplay) {
      video.autoplay = true;
      autoplayVideos.push(video);
    }
    video.load();
  });
  return autoplayVideos;
}

function scheduleMutedAutoplay(videos) {
  videos.forEach((video) => {
    let started = false;
    const tryPlay = () => {
      if (started) return;
      started = true;
      video.play().catch(() => {});
    };
    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
      video.addEventListener('loadeddata', tryPlay, { once: true });
    }
  });
}

/**
 * Custom Element for Social Media Photos
 */
class CustomSocialMediaPhotos extends HTMLElement {
  constructor() {
    super();
    this.manager = new SocialMediaPhotosManager();
    this._lazyVideoIo = null;
  }

  connectedCallback() {
    // Initialize any custom functionality for social media photos
    const photoItems = this.querySelectorAll('.social-media-photos__item');
    
    if (photoItems.length === 0) {
      const section = this.closest('.social-media-photos');
      if (section && !section.querySelector('.social-media-photos__placeholder')) {
        section.style.display = 'none';
      }
      return;
    }

    const lazyVideos = this.querySelectorAll('video[data-lazy-video-sources]');
    if (lazyVideos.length === 0) {
      this.manager.initializeVideoPlayers(this);
      return;
    }

    const startPlayers = () => {
      const toAutoplay = hydrateLazyVideos(this);
      this.manager.initializeVideoPlayers(this);
      scheduleMutedAutoplay(toAutoplay);
    };

    if ('IntersectionObserver' in window) {
      this._lazyVideoIo = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          this._lazyVideoIo = null;
          startPlayers();
        },
        { root: null, rootMargin: '150px', threshold: 0 }
      );
      this._lazyVideoIo.observe(this);
    } else {
      startPlayers();
    }
  }

  disconnectedCallback() {
    if (this._lazyVideoIo) {
      this._lazyVideoIo.disconnect();
      this._lazyVideoIo = null;
    }
    // Clean up when element is removed
    if (this.manager) {
      this.manager.destroy();
    }
  }
}

// Register custom element if not already registered
if (!customElements.get('custom-social-media-photos')) {
  customElements.define('custom-social-media-photos', CustomSocialMediaPhotos);
}
